import type { InternalAxiosRequestConfig } from 'axios';

export interface ShopifyRequestConfig extends InternalAxiosRequestConfig {
    shopifySessionRevision?: number;
    shopifySessionRetried?: boolean;
}

export interface MerchantSession {
    shopifySession: boolean;
    sessionRevision: number;
}

export async function withShopifySessionAuthorization(
    config: ShopifyRequestConfig, session: MerchantSession, idToken: () => Promise<string>, apiBaseUrl: string,
) {
    if (!session.shopifySession || (config.headers.has('Authorization') && config.shopifySessionRevision === undefined)) return config;
    // Only our API origin may receive a Shopify bearer token.
    const base = new URL(apiBaseUrl, window.location.origin);
    const target = new URL(config.url || '', config.baseURL ? new URL(config.baseURL, base) : base);
    if (target.origin !== base.origin) throw new Error('Shopify authentication is restricted to the app API.');
    const revision = session.sessionRevision;
    const token = await idToken();
    if (!token || !session.shopifySession || session.sessionRevision !== revision) throw new Error('The Shopify session changed. Reopen the app.');
    config.headers.set('Authorization', `Bearer ${token}`);
    config.shopifySessionRevision = revision;
    return config;
}

export function isCurrentShopifyRequest(config: ShopifyRequestConfig | undefined, session: MerchantSession) {
    return session.shopifySession && config?.shopifySessionRevision === session.sessionRevision;
}

export function shouldRetryShopifyRequest(status: number | undefined, retryHeader: unknown,
    config: ShopifyRequestConfig | undefined, session: MerchantSession) {
    return status === 401 && String(retryHeader) === '1' && isCurrentShopifyRequest(config, session) && !config?.shopifySessionRetried;
}
